import { V2OrderSideType } from '@flipster/utils/types/base';
import { valueof } from '@flipster/utils/typescript';

import { OrderBase, ReqDatetime, ReqPagination, ResPagination } from '../../types';

export interface HistoryBaseParams extends ReqPagination, ReqDatetime {
  symbol?: string;
}

export enum ProductType {
  Perpetual = 'PRODUCT_TYPE_PERPETUAL_SWAP',
  Spot = 'PRODUCT_TYPE_SPOT',
}

export interface PositionHistory {
  positionId: string;
  symbol: string;
  side: V2OrderSideType;
  avgOpenPrice: string;
  avgClosePrice: string;
  qty: string;
  openTime: string;
  closeTime: string;
  isCrossMargin: boolean;
  initLeverage: number;
  isBestTrade: boolean;
  boostApplied: boolean;
  fundingFee: string;
  fundingFeeAqxpRedeemed: string | undefined;
  tradingFee: string;
  tradingFeeVoucherRedeemed: string | undefined;
  tradingFeeAqxpRedeemed: string | undefined;
  grossRealizedPnl: string; // from realizedTradePnl
  netRealizedPnl: string; // from realizedPnl
  netRealizedPnlPct: string; // from realizedPnlPct
  realizedPnl: string; // will be deprecated
  realizedPnlPct: string; // will be deprecated
}

export interface HighestPnlPosition {
  position: PositionHistory;
}

export interface GetPositionHistoryListParams extends ReqPagination {
  symbol?: string;
  startTime?: string;
  endTime?: string;
  prevKey?: string;
  nextKey?: string;
  isAirdrop?: boolean;
}

export interface PositionHistoryList extends ResPagination {
  prevKey: string;
  nextKey: string;
  history: PositionHistory[];
}

export interface GetFillHistoriesParams extends HistoryBaseParams {
  orderId?: string;
  execType?: string;
  productType?: ProductType;
}

export const ExecTypeEnum = {
  New: 'EXEC_TYPE_NEW',
  Invalid: 'EXEC_TYPE_INVALID',
  Trade: 'EXEC_TYPE_TRADE',
  Canceled: 'EXEC_TYPE_CANCELED',
  Replaced: 'EXEC_TYPE_REPLACED',
  Rejected: 'EXEC_TYPE_REJECTED',
} as const;

export type ExecTypeType = valueof<typeof ExecTypeEnum>;

export interface FillHistory extends OrderBase {
  aggressorIndicator: boolean;
  execFee: string;
  execFeeAqxpRedeemed?: string;
  execFeeVoucherRedeemed?: string;
  execId: string;
  execType: ExecTypeType;
  feeRate: string;
  lastPrice: string;
  lastQty: string;
  lastTrade: boolean;
  matchId: string;
  matchTime: number;
  settlementCurrency: string;
}

export interface GetFillHistoriesResponse extends ResPagination {
  history: FillHistory[];
}

export interface GetOrderHistoriesParams extends HistoryBaseParams {
  hideCanceledOrder?: boolean;
  productType?: ProductType;
}

export interface GetOrderHistoriesResponse extends ResPagination {
  history: OrderHistory[];
}

export const TriggerPriceDirectionEnum = {
  Invalid: 'TRIGGER_PRICE_DIRECTION_INVALID',
  Up: 'TRIGGER_PRICE_DIRECTION_UP',
  Down: 'TRIGGER_PRICE_DIRECTION_DOWN',
} as const;

export type TriggerPriceDirectionType = valueof<typeof TriggerPriceDirectionEnum>;

export const TriggerPriceTypeEnum = {
  Invalid: 'TRIGGER_PRICE_TYPE_INVALID',
  MarkPrice: 'TRIGGER_PRICE_TYPE_MARK',
  LastPrice: 'TRIGGER_PRICE_TYPE_LAST_TRADE',
} as const;

export type TriggerPriceTypeType = valueof<typeof TriggerPriceTypeEnum>;

export type OrderLinkType = { type: string; orderId: string };

export interface OrderHistory extends OrderBase {
  cumFee: string;
  cumFeeAqxpRedeemed?: string;
  cumFeeVoucherRedeemed?: string;
  initLeverage: number | null; // spot의 경우 initLeverage가 null
  link: OrderLinkType[];
  orderAmount: string;
  triggerPrice: string;
  triggerPriceDirection: TriggerPriceDirectionType;
  triggerPriceType: TriggerPriceTypeType;
  triggerSymbol: string;
  triggered: boolean;
}
